import { IPlanningSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { IBadge } from "@gtmhub/badges";
import { localize } from "@gtmhub/localization";
import { IRole } from "@gtmhub/roles";
import { ITag } from "@gtmhub/tags";
import { statusOfTask } from "@gtmhub/tasks/utils/tasks-status";
import { IdMap } from "@gtmhub/util/utils";
import { INgRedux } from "@gtmhub/state-management";
import { getLatestSnapshotCustomFieldValue } from "../controllers/helpers";
import { fieldNamesToBeFormattedAsPercentage, IChartPointOptions, IOKRBaseFilterView, ListFilterTypes, ListItem, ListsPropertyNames, ListTypes } from "../models/models";
import { AssigneeActions, IAssignee, IAssigneesStoreState } from "@gtmhub/assignees";
import { parseInlineMentioning } from "@gtmhub/shared/mentions/mentioning";

export type ChartPointType = string | number | boolean | null | Date | ChartPointType[];

const defaultEmptyCategoryValue = "None";

const bindStateToThis = (state: ISessionsStoreState & IAssigneesStoreState): Record<string, unknown> => {
  return {
    sessionsMap: state.sessions.map,
    assigneesMap: state.assignees.map,
  };
};

export class ChartPointsService {
  private sessionsMap: Record<string, IPlanningSession>;
  private assigneesMap: Record<string, IAssignee>;

  static $inject = ["$ngRedux", "PlanningSessionsActions", "AssigneeActions"];

  constructor($ngRedux: INgRedux, sessionActions: PlanningSessionsActions, assgineesActions: AssigneeActions) {
    $ngRedux.connect(bindStateToThis)(this);
    $ngRedux.dispatch(sessionActions.getSessionsIfMissing());
    $ngRedux.dispatch(assgineesActions.getAssignees());
  }

  public listItemToChartPoints(
    item: ListItem,
    chartProperties: { chartValue: IChartPointOptions; chartCategory: IChartPointOptions },
    listType: ListTypes,
    goalsAndMetricsIdMap: IdMap<IOKRBaseFilterView>
  ): [ChartPointType, ChartPointType][] {
    const value = this.getValue(item, chartProperties.chartValue, listType, goalsAndMetricsIdMap);
    if (this.isEmpty(value)) {
      return [];
    }

    let category = this.getValue(item, chartProperties.chartCategory, listType, goalsAndMetricsIdMap);
    if (Array.isArray(category)) {
      category = category.sort().join();
    }
    if (this.isEmpty(category)) {
      category = defaultEmptyCategoryValue;
    }

    if (Array.isArray(value)) {
      // example:
      // Show me: OKRs | Horizontal axis: OKR | Values: Owners | Aggregation: Count
      // we store owners like array and we have only 1 row for an okr even if it has more than one owners,
      // so for each owner we need to add additional row so the aggregation is applied correctly
      return value.map((x) => [category, x]);
    }

    return [[category, value]];
  }

  private isEmpty(toCheck: ChartPointType): boolean {
    return toCheck === null || toCheck === undefined || (typeof toCheck === "string" && toCheck.trim() === "");
  }

  private getValue(item: ListItem, column: IChartPointOptions, listType: ListTypes, goalsAndMetricsIdMap: IdMap<IOKRBaseFilterView>): ChartPointType {
    switch (column.dataType) {
      case ListFilterTypes.alphabetic:
        return this.extractAlpahbeticPoint(item, listType, column, goalsAndMetricsIdMap);
      case ListFilterTypes.id:
        return this.extractIdPoint(item, column.fieldName, goalsAndMetricsIdMap);
      case ListFilterTypes.selectassignee:
        return (this.extractCustomFieldPoints(item, column.fieldName) as ChartPointType[])?.map((value) => this.assigneesMap[value as string]?.name);
      case ListFilterTypes.date:
        if (column.isCustom) {
          return this.dateStringToDate(this.extractCustomFieldPoints(item, column.fieldName) as string);
        }
        return this.dateStringToDate(this.retrieveNestedValue(item, column.fieldName) as string);
      case ListFilterTypes.numeric:
        if (fieldNamesToBeFormattedAsPercentage.has(column.fieldName)) {
          return this.numericToPercentage(this.retrieveNestedValue(item, column.fieldName) as number);
        } else {
          return this.retrieveNestedValue(item, column.fieldName) || 0;
        }
      case ListFilterTypes.dropdown:
        return this.extractCustomFieldPoints(item, column.fieldName);
      case ListFilterTypes.boolean:
        if (column.isCustom) {
          return this.extractCustomFieldPoints(item, column.fieldName);
        }
        return this.retrieveNestedValue(item, column.fieldName);
      case ListFilterTypes.multiselect:
        return this.extractCustomFieldPoints(item, column.fieldName);
      case ListFilterTypes.array:
        return this.extractArrayPoints(item, column.fieldName);
    }
    return null;
  }

  private extractAlpahbeticPoint(item: ListItem, listType: ListTypes, column: IChartPointOptions, goalsAndMetricsIdMap: IdMap<IOKRBaseFilterView>): ChartPointType {
    switch (column.fieldName) {
      case ListsPropertyNames.workflowStatus:
        return localize(this.retrieveNestedValue(item, column.fieldName) as string);
      case ListsPropertyNames.confidenceReason:
      case ListsPropertyNames.name:
      case ListsPropertyNames.title:
      case ListsPropertyNames.firstName:
      case ListsPropertyNames.lastName:
        return this.retrieveNestedValue(item, column.fieldName);
      case ListsPropertyNames.closedStatus:
        if (listType === ListTypes.Task) {
          return goalsAndMetricsIdMap[this.retrieveNestedValue(item, ListsPropertyNames.parentId) as string]?.closedStatus?.status;
        }
        return this.retrieveNestedValue(item, column.fieldName + ".status");
      case ListsPropertyNames.goalClosedStatus:
        return this.retrieveNestedValue(item, column.fieldName + ".status");
      case ListsPropertyNames.targetOperator:
        return this.targetOperatorPoint(item, column.fieldName);
      case ListsPropertyNames.status:
        return statusOfTask(item);
      case ListsPropertyNames.avatar:
      case ListsPropertyNames.picture:
        return null;
      case ListsPropertyNames.lastComment:
        return this.retrieveNestedValue(item, column.fieldName + ".text");
      default:
        if (column.fieldName.split(".")[0] === "latestSnapshot") {
          return getLatestSnapshotCustomFieldValue(column.fieldName, item["latestSnapshot"]);
        } else {
          if (column.isCustom) {
            return parseInlineMentioning(this.retrieveNestedValue(item, "customFields." + this.customFieldNameFromColumn(column.fieldName)) as string, this.assigneesMap);
          } else {
            return parseInlineMentioning(this.retrieveNestedValue(item, column.fieldName) as string, this.assigneesMap);
          }
        }
    }
  }

  private targetOperatorPoint(item: ListItem, fieldName: string): ChartPointType {
    switch (this.retrieveNestedValue(item, fieldName)) {
      case "at_least":
        if (item["manualType"] === "double" || item["manualType"] === "" || (item["insightName"] && item["insightName"] !== "")) {
          return localize("should_increase_to");
        } else if (item["manualType"] === "boolean") {
          return localize("achieved_or_not");
        }
        return null;
      case "at_most":
        return localize("should_decrease_to");
      case "should_stay_above":
        return localize("should_stay_above");
      case "should_stay_below":
        return localize("should_stay_below");
    }
    return null;
  }

  private extractIdPoint(item: ListItem, fieldName: string, goalsAndMetricsIdMap: IdMap<IOKRBaseFilterView>): ChartPointType {
    switch (fieldName) {
      case ListsPropertyNames.ownerIds:
      case ListsPropertyNames.goalOwnerIds:
      case ListsPropertyNames.teamIds:
      case ListsPropertyNames.members:
        return (this.retrieveNestedValue(item, fieldName) as ChartPointType[]).map((value) => this.assigneesMap[value as string]?.name);
      case ListsPropertyNames.ownerId:
      case ListsPropertyNames.createdById:
      case ListsPropertyNames.goalOwnerId:
      case ListsPropertyNames.manager:
        return this.assigneesMap[this.retrieveNestedValue(item, fieldName) as string]?.name;
      case ListsPropertyNames.userRoles:
        return this.retrieveRoles(item)
          .filter((role) => role.team === null || role.team === undefined)
          .map((role) => role.name);
      case ListsPropertyNames.parentGoalSessionId:
      case ListsPropertyNames.sessionId:
        return this.sessionsMap[this.retrieveNestedValue(item, fieldName) as string]?.title;
      case ListsPropertyNames.parentId:
      case ListsPropertyNames.goalId:
        return goalsAndMetricsIdMap[this.retrieveNestedValue(item, fieldName) as string]?.name;
    }
    return null;
  }

  private extractArrayPoints(item: ListItem, fieldName: string): ChartPointType {
    switch (fieldName) {
      case ListsPropertyNames.tags:
        return this.retrieveTags(item).map((tag) => tag.title);
      case ListsPropertyNames.badges:
        return this.retrieveBadges(item).map((badge) => badge.name);
      default:
        return this.retrieveNestedValue(item, fieldName);
    }
  }

  private extractCustomFieldPoints(item: ListItem, columnName: string): ChartPointType {
    const rootField = columnName.split(".")[0];
    if (rootField === "customFields") {
      return this.retrieveNestedValue(item, rootField + "." + this.customFieldNameFromColumn(columnName));
    }
    if (rootField === "latestSnapshot") {
      return this.retrieveNestedValue(item, rootField + ".customFields." + this.customFieldNameFromColumn(columnName));
    }
  }

  private retrieveNestedValue(item: ListItem, nestedPath: string): ChartPointType {
    const splitted = nestedPath.split(".");
    let value = item[splitted[0]];

    for (let i = 1; i < splitted.length; i++) {
      if (!value) {
        break;
      }
      value = value[splitted[i]];
    }
    return value;
  }

  private retrieveRoles(item: ListItem): IRole[] {
    return item[ListsPropertyNames.roles];
  }

  private retrieveTags(item: ListItem): ITag[] {
    return item[ListsPropertyNames.tags];
  }

  private retrieveBadges(item: ListItem): IBadge[] {
    return item[ListsPropertyNames.badges];
  }

  private dateStringToDate(dateString: string): Date {
    if (dateString === undefined || dateString === null || dateString.length === 0 || dateString === "0001-01-01T00:00:00Z") {
      return null;
    }
    const date = new Date(dateString);
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  }

  private numericToPercentage(number: number): number {
    return number * 100;
  }

  private customFieldNameFromColumn(columnName: string): string {
    return columnName.split(".").pop();
  }
}
